import { Injectable, inject } from '@angular/core';
import { LngLat, LngLatBounds, Map, GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl';
import { Subscription, timer } from 'rxjs';
import device from 'current-device';
import { MapService } from './map.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { SettingsService, MapSkin } from '../account/settings.service';
import { AccountService, SubscriptionType } from '../account/account.service';
import { ChargingGroup, ChargingStation, ChargingStationDetails, Plug } from 'src/app/models/charging-station';
import { Route } from 'src/app/models/route';
import { MapChargingPopupService, PopupAlign } from './map-charging-popup.service';
import { getMapSymbolScale } from './map-symbol-scale';
import { DisplayMode } from 'src/app/models/display-mode';
import { MapElementType } from 'src/app/models/map-element-type';
import { VehicleService } from '../vehicle/vehicle.service';

// Source and layer IDs
const CS_SOURCE = 'cs-source';
const CS_FAV_SOURCE = 'cs-fav-source';
const CS_GROUPS_SOURCE = 'cs-groups-source';

const CS_GROUPS_LAYER = 'cs-groups-layer';
const CS_LAYER = 'cs-layer';
const CS_FAV_LAYER = 'cs-fav-layer';

// Charging group circle colors by powerid and skin
const GROUP_COLORS: { [key: string]: string } = {
  '1_dark': '#b8b752',
  '2_dark': '#65b17c',
  '3_dark': '#b165ab',
  '1_light': '#464646',
  '2_light': '#218f42',
  '3_light': '#8f2186'
};

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

type StationBadge = 'none' | 'fav-station' | 'dis-station' | 'fav-operator' | 'dis-operator';
type StatusDot = 'none' | 'available' | 'unavailable';

const STATUS_DOTS: readonly StatusDot[] = ['none', 'available', 'unavailable'];
const STATUS_SUFFIX: Readonly<Record<StatusDot, string>> = {
  none: '',
  available: '_avail',
  unavailable: '_unavail'
};

/** Every station pin the app can draw, before skin and status are applied. */
const STATION_ICONS: readonly { base: string, dark: string, light: string, badge: StationBadge }[] = [
  // Normal
  { base: 'cs_big1',        dark: '#b8b752', light: '#9a9606', badge: 'none' },
  { base: 'cs_big2',        dark: '#65b17c', light: '#218f42', badge: 'none' },
  { base: 'cs_big3',        dark: '#b165ab', light: '#8f2186', badge: 'none' },
  // Favorite station (heart)
  { base: 'cs_big1_favs',   dark: '#b8b752', light: '#9a9606', badge: 'fav-station' },
  { base: 'cs_big2_favs',   dark: '#65b17c', light: '#218f42', badge: 'fav-station' },
  { base: 'cs_big3_favs',   dark: '#b165ab', light: '#8f2186', badge: 'fav-station' },
  // Disabled station (ban)
  { base: 'cs_big1_diss',   dark: '#b8b752', light: '#9a9606', badge: 'dis-station' },
  { base: 'cs_big2_diss',   dark: '#65b17c', light: '#218f42', badge: 'dis-station' },
  { base: 'cs_big3_diss',   dark: '#b165ab', light: '#8f2186', badge: 'dis-station' },
  // Favorite operator (star)
  { base: 'cs_big1_favo',   dark: '#b8b752', light: '#9a9606', badge: 'fav-operator' },
  { base: 'cs_big2_favo',   dark: '#65b17c', light: '#218f42', badge: 'fav-operator' },
  { base: 'cs_big3_favo',   dark: '#b165ab', light: '#8f2186', badge: 'fav-operator' },
  // Disabled operator (gray, no badge)
  { base: 'cs_big1_diso',   dark: '#b8b752', light: '#9a9606', badge: 'dis-operator' },
  { base: 'cs_big2_diso',   dark: '#65b17c', light: '#218f42', badge: 'dis-operator' },
  { base: 'cs_big3_diso',   dark: '#b165ab', light: '#8f2186', badge: 'dis-operator' }
];

/**
 * How many count-specific group marker images to keep registered on the map.
 *
 * These are keyed by cluster count, which is unbounded, so without a ceiling a
 * few minutes of panning registers hundreds of ~48 KB RGBA images that MapLibre
 * never releases — in the JS heap and in the icon atlas texture. The cap sits
 * comfortably above the number of clusters on screen at once, so in practice it
 * only ever evicts markers the user has panned away from.
 */
const MAX_GROUP_IMAGES = 48;

@Injectable({
  providedIn: 'root',
})
export class MapChargingStationDrawerService {

  private mapInstance: Map | null = null;
  private imagesLoaded = false;

  // Charging station state
  private chargingStationsDetailed: boolean = false;
  private favoriteChargingStationsDetailed: boolean = false;
  private chargingStationRecalc: boolean = false;
  private showOnMap: boolean = false;

  private chargingStationsInsideBoundingBox: ChargingStation[] = [];
  private favoriteChargingStationsInsideBoundingBox: ChargingStation[] = [];
  private chargingGroupsInsideBoundingBox: ChargingGroup[] = [];

  private bigChargingStations: number[][] = [];
  private routeChargingStationIds: Set<string> = new Set();

  private lastBoundingBox: { south: number, west: number, north: number, east: number } | null = null;
  private lastTileBounds: string[] = [];
  private chargingStationBoundStatus: any = {};
  private chargerStatusTimer = new Subscription();

  // Track the station whose popup is currently open so its icon stays visible at all zoom levels
  private popupStationId: string | null = null;
  private popupStationCache: ChargingStation | null = null;

  private subscriptions: Subscription[] = [];

  /** Teardown callbacks for every map event handler registered by init(). */
  private mapListeners: (() => void)[] = [];

  /**
   * Station icon id -> how to rasterise it, for the currently loaded skin.
   * A plain record rather than a Map because maplibre's `Map` shadows the
   * built-in one throughout this file.
   */
  private stationImageSpecs: { [id: string]: { color: string, isDark: boolean, badge: StationBadge, dot: StatusDot } } = {};

  /** Registered group marker image ids, least-recently-used first. */
  private groupImageUsage = new Set<string>();

  private readonly mapService = inject(MapService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly accountService = inject(AccountService);
  private readonly mapChargingPopupService = inject(MapChargingPopupService);
  private readonly vehicleService = inject(VehicleService);

  public init(map: Map): void {
    // init() runs again after every style reload — a skin change or a pointer
    // scale flip, see MapComponent.reinitMapLayersAfterStyleReload(). MapLibre
    // event handlers and RxJS subscriptions both survive setStyle(), so without
    // this each reload would stack another full set on top of the last: every
    // pan would recalculate the bounding box and refetch tiles N times over, and
    // the old closures would never be collected.
    this.releaseMapListeners();
    this.releaseSubscriptions();

    this.mapInstance = map;
    this.mapChargingPopupService.init(map);
    this.loadMapImages();
    this.setupSources();
    this.setupLayers();
    this.setupClickHandlers();
    this.initChargingStationEvents();
    this.initChargingStationSubscriptions();

    this.subscriptions.push(this.mapService.ObservableSearchedMapElement.subscribe((resp) => {
      if (!resp || !resp.elementType || !this.mapInstance) return;

      if (resp.elementType === MapElementType.FavoriteChargingStation ||
          resp.elementType === MapElementType.DisabledChargingStation) {
        // Revealed from the settings dialog: lock the popup so a map click can't
        // dismiss it (cleared on Back via clearShownChargingStation()).
        this.showOnMap = true;
        this.mapInstance.flyTo({ center: [resp.mapElement.lon, resp.mapElement.lat], zoom: 15, essential: true });

        this.popupStationId = resp.mapElement.id;
        this.popupStationCache = resp.mapElement;
        this.collectChargingStationsInsideBoundingBox();

        this.mapChargingPopupService.loadChargingStationPopup(PopupAlign.Top, 0, 20, resp.mapElement.id, false, false, true);
      }

      if (resp.elementType === MapElementType.Chargingstation) {
        this.popupStationId = resp.mapElement.id;
        this.popupStationCache = resp.mapElement;
        this.collectChargingStationsInsideBoundingBox();
        this.mapChargingPopupService.loadChargingStationPopup(PopupAlign.Top, 0, 80, resp.mapElement.id, true, false, false);
      }
    }));

    this.subscriptions.push(this.mapChargingPopupService.statusChanged$.subscribe(() => {
      this.chargingStationRecalc = true;
      this.collectChargingStationsInsideBoundingBox();
      if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.ROUTE) {
        const lastPopup = this.mapChargingPopupService.getLastOpenedPopup();
        if (lastPopup && !this.showOnMap) {
          this.mapChargingPopupService.redrawChargingStationPopup(lastPopup);
        }
      }
    }));

    // Set initial detail flags based on current zoom and trigger first load
    const zoom = map.getZoom();
    if (zoom > 11) this.chargingStationsDetailed = true;
    if (zoom > 7) this.favoriteChargingStationsDetailed = true;
    this.getExtendedBoundingBox();
  }

  /**
   * Closes a charging-station popup that was locked open from the settings
   * dialog and releases the lock. Called when the charger panel is dismissed
   * (Back). No-op when nothing is locked.
   */
  public clearShownChargingStation(): void {
    if (!this.showOnMap) return;
    this.showOnMap = false;
    this.mapChargingPopupService.removeChargingStationPopup();
    this.popupStationId = null;
    this.popupStationCache = null;
    this.collectChargingStationsInsideBoundingBox();
  }

  public destroy(): void {
    this.popupStationId = null;
    this.popupStationCache = null;
    this.releaseMapListeners();
    this.releaseSubscriptions();
    this.mapChargingPopupService.destroy();
    this.removeLayers();
    this.removeSources();
    this.stationImageSpecs = {};
    this.groupImageUsage.clear();
    this.mapInstance = null;
    this.imagesLoaded = false;
  }

  /** Detach every map event handler registered by init(). */
  private releaseMapListeners(): void {
    this.mapListeners.forEach(off => off());
    this.mapListeners = [];
  }

  private releaseSubscriptions(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.chargerStatusTimer.unsubscribe();
  }

  /**
   * map.on() plus the matching map.off() recorded for teardown. The maplibre
   * overloads (with and without a layer id) do not survive being forwarded
   * through a wrapper, so the casts are contained here rather than at each call.
   */
  private addMapListener(type: string, layerOrHandler: unknown, handler?: unknown): void {
    const map = this.mapInstance!;
    const on = map.on.bind(map) as (...args: unknown[]) => unknown;
    const off = map.off.bind(map) as (...args: unknown[]) => unknown;

    if (handler === undefined) {
      on(type, layerOrHandler);
      this.mapListeners.push(() => off(type, layerOrHandler));
    } else {
      on(type, layerOrHandler, handler);
      this.mapListeners.push(() => off(type, layerOrHandler, handler));
    }
  }

  // ─── Map Image Loading ───────────────────────────────────────────────

  /**
   * Register every station icon the current skin can produce, and rasterise only
   * the ones that are on screen from the first frame.
   *
   * This is the most expensive step of init(), which is why MapComponent defers
   * that call past the first paint. It used to build all 90 icons eagerly
   * (15 pins x 2 skins x 3 status variants), each one a canvas draw plus a
   * getImageData() readback — on a phone, where getMapSymbolScale() also bumps
   * every canvas to 100x120, that was a main-thread stall of real length.
   *
   * Two thirds of that work was never needed:
   *  - Only one skin is ever displayed, and switching skins reloads the style
   *    (which drops every registered image) and re-runs init() anyway.
   *  - The _avail/_unavail variants only appear once the charger-status poll
   *    returns data, which is gated on a non-Free subscription. They are
   *    registered here but rasterised by ensureStationImage() on first use.
   *
   * Net: 15 rasterisations at start-up instead of 90.
   */
  private loadMapImages(): void {
    // setStyle() drops every registered image and init() re-runs afterwards, so
    // the bookkeeping has to be reset or we would believe icons are still there.
    this.stationImageSpecs = {};
    this.groupImageUsage.clear();

    const isLight = this.settingsService.getMap().Skin === MapSkin.Light;
    const skinSuffix = isLight ? '_light' : '';

    for (const cfg of STATION_ICONS) {
      const color = isLight ? cfg.light : cfg.dark;

      for (const dot of STATUS_DOTS) {
        this.stationImageSpecs[`${cfg.base}_200${skinSuffix}${STATUS_SUFFIX[dot]}`] =
          { color, isDark: !isLight, badge: cfg.badge, dot };
      }

      this.ensureStationImage(`${cfg.base}_200${skinSuffix}`);
    }

    this.imagesLoaded = true;
  }

  /**
   * Rasterise a registered station icon unless the map already has it. Safe and
   * cheap to call per feature on every source update — hasImage() is the fast
   * path, and an unknown id (a stale skin suffix mid-reload) is simply ignored.
   */
  private ensureStationImage(id: string): void {
    const map = this.mapInstance;
    if (!map || map.hasImage(id)) return;

    const spec = this.stationImageSpecs[id];
    if (!spec) return;

    map.addImage(
      id,
      this.generateStationMarkerImage(spec.color, spec.isDark, spec.badge, spec.dot),
      { pixelRatio: 2 }
    );
  }

  /**
   * Register the count-specific group marker image for `id` if needed, and mark
   * it as most recently used. Re-inserting into the Set moves it to the end,
   * which is what makes iteration order least-recently-used first.
   */
  private touchGroupImage(id: string, group: ChargingGroup, skinStr: string, isLight: boolean): void {
    const map = this.mapInstance!;

    this.groupImageUsage.delete(id);
    this.groupImageUsage.add(id);

    if (!map.hasImage(id)) {
      map.addImage(
        id,
        this.generateGroupMarkerImage(GROUP_COLORS[`${group.powerid}_${skinStr}`], !isLight, group.count),
        { pixelRatio: 2 }
      );
    }
  }

  /**
   * Drop the least recently used group images until the cache is back under
   * MAX_GROUP_IMAGES. Ids in `live` are on screen right now and are skipped, so
   * a view holding more clusters than the cap simply keeps them all rather than
   * evicting an icon it is about to draw.
   */
  private evictGroupImages(live: Set<string>): void {
    const map = this.mapInstance!;

    while (this.groupImageUsage.size > MAX_GROUP_IMAGES) {
      let evicted = false;

      for (const id of this.groupImageUsage) {
        if (live.has(id)) continue;
        this.groupImageUsage.delete(id);
        if (map.hasImage(id)) map.removeImage(id);
        evicted = true;
        break;
      }

      if (!evicted) return; // everything left is in use
    }
  }

  private generateGroupMarkerImage(color: string, isDark: boolean, count: number): { width: number, height: number, data: Uint8Array } {
    // Retina scale. addImage() below uses pixelRatio 2, so folding the touch
    // scale in here grows the displayed pin without eroding the 2× oversample.
    const s = 2 * getMapSymbolScale();
    // Fully circular grouped-station marker
    const cw = 44 * s; // 88 canvas px at scale 1
    const ch = 44 * s; // 88 canvas px at scale 1

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    // willReadFrequently keeps the canvas on the CPU backend. Every one of these
    // markers is immediately read back with getImageData(), which on an
    // accelerated canvas forces a GPU->CPU sync that dominates map start-up on a
    // phone. See renderStationMarkerCanvas() for the same reasoning.
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

    const colorR = parseInt(color.slice(1, 3), 16);
    const colorG = parseInt(color.slice(3, 5), 16);
    const colorB = parseInt(color.slice(5, 7), 16);

    const cx = cw / 2;
    const circleCY = ch / 2; // circle centre y (vertically centred)
    const circleR  = 20 * s; // circle fills almost full width

    const drawShape = () => {
      ctx.beginPath();
      ctx.arc(cx, circleCY, circleR, 0, Math.PI * 2);
      ctx.closePath();
    };

    // 1. Semi-transparent shape body (56% opacity, matching SVG fill-opacity="0.56")
    drawShape();
    ctx.fillStyle = `rgba(${colorR},${colorG},${colorB},0.56)`;
    ctx.fill();

    // 2. White inner circle (opacity depends on skin)
    const innerR = Math.round(circleR * 0.63);
    ctx.beginPath();
    ctx.arc(cx, circleCY, innerR, 0, Math.PI * 2);
    ctx.fillStyle = isDark ? '#CFD9E4' : '#ffffff';
    ctx.fill();

    // 3. Count number centred in the white circle in dark ink
    const countStr = count.toString();
    const fontSize = countStr.length === 1 ? 17 * s : countStr.length === 2 ? 14 * s : 11 * s;
    ctx.font         = `bold ${fontSize}px Roboto, Arial, sans-serif`;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle    = '#151c24';
    ctx.fillText(countStr, cx, circleCY + 1 * s);

    const imageData = ctx.getImageData(0, 0, cw, ch);
    return { width: cw, height: ch, data: new Uint8Array(imageData.data.buffer) };
  }

  private generateStationMarkerImage(
    color: string, isDark: boolean,
    badge: 'none' | 'fav-station' | 'dis-station' | 'fav-operator' | 'dis-operator',
    statusDot: 'none' | 'available' | 'unavailable' = 'none'
  ): { width: number, height: number, data: Uint8Array } {
    const canvas = this.renderStationMarkerCanvas(color, isDark, badge, statusDot);
    const ctx = canvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return { width: canvas.width, height: canvas.height, data: new Uint8Array(imageData.data.buffer) };
  }

  /**
   * Generate a station marker icon as a data URL for use in HTML <img> tags
   * (e.g., charging-options list rows). Caches results.
   */
  private stationIconDataUrlCache: { [key: string]: string } = {};
  public getStationIconDataUrl(
    badge: 'none' | 'fav-station' | 'dis-station' | 'fav-operator' | 'dis-operator',
    powerSuffix: '1' | '2' | '3' = '2'
  ): string {
    const isLight = this.settingsService.getMap().Skin === MapSkin.Light;
    const skinKey = isLight ? 'light' : 'dark';
    const cacheKey = `${badge}_${powerSuffix}_${skinKey}`;
    if (this.stationIconDataUrlCache[cacheKey]) return this.stationIconDataUrlCache[cacheKey];

    const colorTable: { [k: string]: { dark: string, light: string } } = {
      '1': { dark: '#b8b752', light: '#9a9606' },
      '2': { dark: '#65b17c', light: '#218f42' },
      '3': { dark: '#b165ab', light: '#8f2186' }
    };
    const color = isLight ? colorTable[powerSuffix].light : colorTable[powerSuffix].dark;
    const canvas = this.renderStationMarkerCanvas(color, !isLight, badge, 'none');
    const dataUrl = canvas.toDataURL('image/png');
    this.stationIconDataUrlCache[cacheKey] = dataUrl;
    return dataUrl;
  }

  /**
   * Circular station badge (no teardrop / no overlay badge) for use in list rows:
   * a ring in the charger's power-level colour, a light inner disc and the
   * charging-connector pictogram. Returned as an SVG data URL, cached per
   * power-level + skin.
   */
  private stationCircleIconCache: { [key: string]: string } = {};
  public getStationCircleIconDataUrl(powerSuffix: '1' | '2' | '3' = '2'): string {
    const isLight = this.settingsService.getMap().Skin === MapSkin.Light;
    const skinKey = isLight ? 'light' : 'dark';
    const cacheKey = `${powerSuffix}_${skinKey}`;
    if (this.stationCircleIconCache[cacheKey]) return this.stationCircleIconCache[cacheKey];

    const colorTable: { [k: string]: { dark: string, light: string } } = {
      '1': { dark: '#b8b752', light: '#9a9606' },
      '2': { dark: '#65b17c', light: '#218f42' },
      '3': { dark: '#b165ab', light: '#8f2186' }
    };
    const ringColor = isLight ? colorTable[powerSuffix].light : colorTable[powerSuffix].dark;
    const innerColor = isLight ? '#ffffff' : '#CFD9E4';
    const connectorPath =
      'M14.7 18C16.65 18 18 15.75 18 13.95V12.45C18 12.3 17.85 12.15 17.7 12.15H16.35V9.45' +
      'C16.35 9 16.05 9 15.75 9C15.3 9 15 9 15 9.45V12.15H12V9.45C11.85 9 11.7 9 11.25 9' +
      'C10.8 9 10.5 9 10.5 9.45V12.15H9.3C9.15 12.15 9 12.3 9 12.45V14.1C9 15.6 10.2 18 12.15 18H14.7Z';

    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 27 27" fill="none">` +
      `<path d="M13.2684 26.6667C20.5963 26.6667 26.5368 20.6972 26.5368 13.3334C26.5368 5.96955 20.5963 0 13.2684 0C5.94046 0 0 5.96955 0 13.3334C0 20.6972 5.94046 26.6667 13.2684 26.6667Z" fill="${ringColor}"/>` +
      `<path d="M13.3252 21.8348C17.9662 21.8348 21.7285 18.0541 21.7285 13.3903C21.7285 8.72658 17.9662 4.94586 13.3252 4.94586C8.68417 4.94586 4.92188 8.72658 4.92188 13.3903C4.92188 18.0541 8.68417 21.8348 13.3252 21.8348Z" fill="${innerColor}"/>` +
      `<path d="${connectorPath}" fill="#151C24"/>` +
      `</svg>`;

    const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    this.stationCircleIconCache[cacheKey] = dataUrl;
    return dataUrl;
  }

  private renderStationMarkerCanvas(
    color: string, isDark: boolean,
    badge: 'none' | 'fav-station' | 'dis-station' | 'fav-operator' | 'dis-operator',
    statusDot: 'none' | 'available' | 'unavailable'
  ): HTMLCanvasElement {
    const isDisabled = badge === 'dis-station' || badge === 'dis-operator';
    const s = 2 * getMapSymbolScale(); // retina scale; see generateGroupMarkerImage
    const cw = 40 * s; // 80 canvas px at scale 1
    const ch = 48 * s; // 96 canvas px at scale 1

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    // Most callers hand this straight to getImageData(). Opting out of GPU
    // acceleration makes that readback cheap instead of a pipeline stall — the
    // drawing itself is trivial, so there is nothing to lose here.
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

    const colorR = parseInt(color.slice(1, 3), 16);
    const colorG = parseInt(color.slice(3, 5), 16);
    const colorB = parseInt(color.slice(5, 7), 16);

    const cx = cw / 2;
    const circleCY = 19 * s; // circle center y
    const circleR  = 15 * s; // circle radius
    const pinTipY  = 44 * s; // pin tip — shorter drip

    // Compute tangent-contact angles for the teardrop outline
    const d = pinTipY - circleCY;                          // dist from center to tip
    const t = Math.sqrt(d * d - circleR * circleR);        // tangent length
    const startAngle = Math.atan2(circleR, t);             // right contact (lower-right)
    const endAngle   = -(Math.PI + startAngle);            // left contact  (lower-left), via CCW over top

    // Draws the full teardrop pin shape as a closed path
    const drawTeardrop = () => {
      ctx.beginPath();
      ctx.arc(cx, circleCY, circleR, startAngle, endAngle, true); // upper arc
      ctx.lineTo(cx, pinTipY);   // left edge to tip
      ctx.closePath();           // tip back to right contact
    };

    // Muted gray for disabled stations
    const pinR = isDisabled ? (isDark ? 0x6b : 0x9c) : colorR;
    const pinG = isDisabled ? (isDark ? 0x72 : 0xa3) : colorG;
    const pinB = isDisabled ? (isDark ? 0x80 : 0xaf) : colorB;

    // 1. Bottom shadow — lower half of teardrop in full opacity (creates solid depth)
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, circleCY, cw, ch);
    ctx.clip();
    drawTeardrop();
    ctx.fillStyle = `rgb(${pinR},${pinG},${pinB})`;
    ctx.fill();
    ctx.restore();

    // 2. Semi-transparent full teardrop body (56% opacity, matching SVG fill-opacity)
    drawTeardrop();
    ctx.fillStyle = `rgba(${pinR},${pinG},${pinB},0.56)`;
    ctx.fill();

    // 3. White inner circle (opacity depends on skin)
    const innerR = Math.round(circleR * 0.63);
    ctx.beginPath();
    ctx.arc(cx, circleCY, innerR, 0, Math.PI * 2);
    ctx.fillStyle = isDark ? '#CFD9E4' : '#ffffff';
    ctx.fill();

    // 4. Charging connector icon (path from SVG, coords centered at 13.5,13.5 in ~27-unit space)
    const connectorPath = new Path2D(
      'M14.7 18C16.65 18 18 15.75 18 13.95V12.45C18 12.3 17.85 12.15 17.7 12.15H16.35V9.45' +
      'C16.35 9 16.05 9 15.75 9C15.3 9 15 9 15 9.45V12.15H12V9.45C11.85 9 11.7 9 11.25 9' +
      'C10.8 9 10.5 9 10.5 9.45V12.15H9.3C9.15 12.15 9 12.3 9 12.45V14.1C9 15.6 10.2 18 12.15 18H14.7Z'
    );
    // SVG white circle radius is 8.47 units at the same coordinate scale; map to innerR
    const iconScale = innerR / 8.47;
    ctx.save();
    ctx.translate(cx - 13.5 * iconScale, circleCY - 13.5 * iconScale);
    ctx.scale(iconScale, iconScale);
    ctx.fillStyle = isDisabled ? 'rgba(21,28,36,0.35)' : '#151c24';
    ctx.fill(connectorPath);
    ctx.restore();

    // 5. Badge — lower-right quadrant of the circle
    const badgeCX = cx + circleR * 0.7;
    const badgeCY = circleCY + circleR * 0.6;
    const bs = 9 * s;

    if (badge === 'fav-station') {
      // Center the heart vertically around badgeCY (shape extent is bs * 1.1)
      const heartTop = badgeCY - bs * 0.55;
      const drawHeart = () => {
        const topH = bs * 0.3;
        ctx.beginPath();
        ctx.moveTo(badgeCX, heartTop + topH);
        ctx.bezierCurveTo(badgeCX, heartTop, badgeCX - bs * 0.55, heartTop, badgeCX - bs * 0.55, heartTop + topH);
        ctx.bezierCurveTo(badgeCX - bs * 0.55, heartTop + bs * 0.6, badgeCX, heartTop + bs * 0.8, badgeCX, heartTop + bs * 1.1);
        ctx.bezierCurveTo(badgeCX, heartTop + bs * 0.8, badgeCX + bs * 0.55, heartTop + bs * 0.6, badgeCX + bs * 0.55, heartTop + topH);
        ctx.bezierCurveTo(badgeCX + bs * 0.55, heartTop, badgeCX, heartTop, badgeCX, heartTop + topH);
        ctx.closePath();
      };
      drawHeart();
      ctx.lineJoin   = 'round';
      ctx.strokeStyle = isDark ? '#CFD9E4' : '#ffffff';
      ctx.lineWidth  = 5 * s;
      ctx.stroke();
      drawHeart();
      ctx.fillStyle = '#E53535';
      ctx.fill();
    } else if (badge === 'dis-station') {
      const r = bs * 0.5;
      ctx.beginPath();
      ctx.arc(badgeCX, badgeCY, r + 2 * s, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? '#CFD9E4' : '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(badgeCX, badgeCY, r, 0, Math.PI * 2);
      ctx.strokeStyle = '#E53535';
      ctx.lineWidth   = 2.5 * s;
      ctx.stroke();
      const slash = Math.PI * 0.25;
      ctx.beginPath();
      ctx.moveTo(badgeCX - Math.cos(slash) * r, badgeCY - Math.sin(slash) * r);
      ctx.lineTo(badgeCX + Math.cos(slash) * r, badgeCY + Math.sin(slash) * r);
      ctx.strokeStyle = '#E53535';
      ctx.lineWidth   = 2.5 * s;
      ctx.lineCap     = 'round';
      ctx.stroke();
    } else if (badge === 'fav-operator') {
      const starBs = 10 * s;
      const spikes = 5;
      const starOuterR = starBs * 0.5;
      const starInnerR = starOuterR * 0.45;
      const drawStar = () => {
        ctx.beginPath();
        for (let i = 0; i < spikes * 2; i++) {
          const angle = (i * Math.PI / spikes) - Math.PI / 2;
          const rr = i % 2 === 0 ? starOuterR : starInnerR;
          i === 0
            ? ctx.moveTo(badgeCX + Math.cos(angle) * rr, badgeCY + Math.sin(angle) * rr)
            : ctx.lineTo(badgeCX + Math.cos(angle) * rr, badgeCY + Math.sin(angle) * rr);
        }
        ctx.closePath();
      };
      drawStar();
      ctx.lineJoin    = 'round';
      ctx.strokeStyle = isDark ? '#CFD9E4' : '#ffffff';
      ctx.lineWidth   = 3.5 * s;
      ctx.stroke();
      drawStar();
      ctx.fillStyle = '#D4A200';
      ctx.fill();
    }

    // 6. Status dot — upper-right corner matching SVG position (cx=20.72/27, cy=4.41/37)
    if (statusDot !== 'none') {
      const dotCX    = Math.round(cw * 0.767);  // 76.7% from left
      const dotCY    = Math.round(ch * 0.119);  // 11.9% from top
      const dotOuterR = Math.round(5.5 * s);
      const dotInnerR = Math.round(4 * s);
      ctx.beginPath();
      ctx.arc(dotCX, dotCY, dotOuterR, 0, Math.PI * 2);
      ctx.fillStyle = isDark ? '#CFD9E4' : '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(dotCX, dotCY, dotInnerR, 0, Math.PI * 2);
      ctx.fillStyle = statusDot === 'available' ? '#4ACD33' : '#ec0002';
      ctx.fill();
    }

    return canvas;
  }

  // ─── Map Sources ─────────────────────────────────────────────────────

  private setupSources(): void {
    const map = this.mapInstance!;
    if (!map.getSource(CS_SOURCE)) map.addSource(CS_SOURCE, { type: 'geojson', data: EMPTY_FC });
    if (!map.getSource(CS_FAV_SOURCE)) map.addSource(CS_FAV_SOURCE, { type: 'geojson', data: EMPTY_FC });
    if (!map.getSource(CS_GROUPS_SOURCE)) map.addSource(CS_GROUPS_SOURCE, { type: 'geojson', data: EMPTY_FC });
  }

  private removeSources(): void {
    const map = this.mapInstance;
    if (!map) return;
    for (const id of [CS_SOURCE, CS_FAV_SOURCE, CS_GROUPS_SOURCE]) {
      if (map.getSource(id)) map.removeSource(id);
    }
  }

  // ─── Map Layers ──────────────────────────────────────────────────────

  private setupLayers(): void {
    const map = this.mapInstance!;

    if (map.getLayer(CS_GROUPS_LAYER) || map.getLayer(CS_LAYER) || map.getLayer(CS_FAV_LAYER)) {
      return;
    }

    // Charging groups — teardrop pin with count baked into image
    map.addLayer({
      id: CS_GROUPS_LAYER,
      type: 'symbol',
      source: CS_GROUPS_SOURCE,
      minzoom: 11,
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': ['step', ['zoom'], 0.85, 14, 1.0, 15, 1.1, 17, 1.2],
        'icon-anchor': 'bottom',
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      },
      paint: {}
    });

    // Regular charging stations — teardrop pin with ⚡ icon
    map.addLayer({
      id: CS_LAYER,
      type: 'symbol',
      source: CS_SOURCE,
      minzoom: 11,
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': ['step', ['zoom'], 0.85, 14, 1.0, 15, 1.1, 17, 1.2],
        'icon-anchor': 'bottom',
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      },
      paint: {
        'icon-opacity': ['number', ['get', 'opacity'], 1]
      }
    });

    // Favorite charging stations (visible from zoom 7) — teardrop pin with ⚡ icon
    map.addLayer({
      id: CS_FAV_LAYER,
      type: 'symbol',
      source: CS_FAV_SOURCE,
      minzoom: 7,
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': ['step', ['zoom'], 0.85, 14, 1.0, 15, 1.1, 17, 1.2],
        'icon-anchor': 'bottom',
        'icon-pitch-alignment': 'viewport',
        'icon-rotation-alignment': 'viewport',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    });
  }

  private removeLayers(): void {
    const map = this.mapInstance;
    if (!map) return;
    for (const id of [CS_FAV_LAYER, CS_LAYER, CS_GROUPS_LAYER]) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
  }

  // ─── Click Handlers ──────────────────────────────────────────────────

  private setupClickHandlers(): void {
    const map = this.mapInstance!;

    this.addMapListener('click', CS_LAYER, (e: MapLayerMouseEvent) => {
      if (this.showOnMap || !e.features?.length) return;
      if (this.isCSTooltipClick(e.originalEvent)) return;

      const feature = e.features![0];
      const csId = feature.properties!['id'];
      const coords = (feature.geometry as any).coordinates;

      this.popupStationId = csId;
      this.popupStationCache = this.chargingStationsInsideBoundingBox.find(s => s.id === csId) ?? null;
      this.collectChargingStationsInsideBoundingBox();

      this.mapChargingPopupService.loadChargingStationPopup(PopupAlign.Top, 0, 20, csId, true, true, false)
    });

    this.addMapListener('click', CS_FAV_LAYER, (e: MapLayerMouseEvent) => {
      if (this.showOnMap || !e.features?.length) return;
      if (this.isCSTooltipClick(e.originalEvent)) return;

      const feature = e.features![0];
      const csId = feature.properties!['id'];
      const coords = (feature.geometry as any).coordinates;

      this.popupStationId = csId;
      this.popupStationCache = this.favoriteChargingStationsInsideBoundingBox.find(s => s.id === csId) ?? null;

      this.mapChargingPopupService.loadChargingStationPopup(PopupAlign.Top, 0, 24, csId, true, true, false);
    });

    this.addMapListener('click', CS_GROUPS_LAYER, (e: MapLayerMouseEvent) => {
      if (!e.features?.length) return;
      const coords = (e.features[0].geometry as any).coordinates;
      map.flyTo({ center: [coords[0], coords[1]], zoom: map.getZoom() + 1 });
    });

    // Cursor pointer on hover
    for (const layer of [CS_LAYER, CS_FAV_LAYER, CS_GROUPS_LAYER]) {
      this.addMapListener('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
      this.addMapListener('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
    }

    // Dismiss popup when clicking on empty map area
    this.addMapListener('click', (e: MapLayerMouseEvent) => {
      // While a station is locked open (revealed from settings), a map click
      // must not dismiss it — it closes only when the user navigates back.
      if (this.showOnMap) return;
      if (this.isCSTooltipClick(e.originalEvent)) return;
      const features = map.queryRenderedFeatures(e.point, { layers: [CS_LAYER, CS_FAV_LAYER, CS_GROUPS_LAYER] });
      if (!features.length) {
        this.mapChargingPopupService.removeChargingStationPopup();
        if (this.popupStationId) {
          this.popupStationId = null;
          this.popupStationCache = null;
          this.collectChargingStationsInsideBoundingBox();
        }
      }
    });
  }

  // ─── Charging Station Events & Subscriptions ─────────────────────────

  private initChargingStationEvents(): void {
    const map = this.mapInstance!;

    this.addMapListener('moveend', () => {
      if (this.lastBoundingBox == null || !this.boundsContains(this.lastBoundingBox, map.getBounds())) {
        this.getExtendedBoundingBox();
      }
    });

    this.addMapListener('zoomend', () => {
      const zoom = map.getZoom();

      if (zoom > 11 && !this.chargingStationsDetailed) {
        this.chargingStationsDetailed = true;
      }
      if (zoom <= 11 && this.chargingStationsDetailed) {
        this.chargingStationsDetailed = false;
        this.chargingStationsInsideBoundingBox = [];
        this.chargingGroupsInsideBoundingBox = [];
      }
      if (zoom > 7 && !this.favoriteChargingStationsDetailed) {
        this.favoriteChargingStationsDetailed = true;
      }
      if (zoom <= 7 && this.favoriteChargingStationsDetailed) {
        this.favoriteChargingStationsDetailed = false;
      }

      this.getExtendedBoundingBox();
    });
  }

  private initChargingStationSubscriptions(): void {
    this.subscriptions.push(this.vehicleService.ObservableChargingStations.subscribe((resp) => {
      if (resp != undefined) {
        if (this.chargingStationsDetailed || this.favoriteChargingStationsDetailed) {
          this.getExtendedBoundingBox();
        }
      }
    }));
  }

  // ─── Extended Bounding Box & Tile Loading ────────────────────────────

  private getExtendedBoundingBox(): void {
    const map = this.mapInstance;
    if (!map) return;

    const bounds = map.getBounds();
    const SW = bounds.getSouthWest();
    const NE = bounds.getNorthEast();

    const latDiff = NE.lat - SW.lat;
    const lngDiff = NE.lng - SW.lng;

    const largerSW = { lat: SW.lat - latDiff / 2, lng: SW.lng - lngDiff / 2 };
    const largerNE = { lat: NE.lat + latDiff / 2, lng: NE.lng + lngDiff / 2 };

    this.lastBoundingBox = {
      south: largerSW.lat,
      west: largerSW.lng,
      north: largerNE.lat,
      east: largerNE.lng
    };

    if (this.favoriteChargingStationsDetailed) {
      const neTile = this.utilsService.LatLonToTile(largerNE.lat, largerNE.lng, 256, 128);
      const swTile = this.utilsService.LatLonToTile(largerSW.lat, largerSW.lng, 256, 128);
      const minX = Math.min(swTile[0], neTile[0]);
      const maxX = Math.max(swTile[0], neTile[0]);
      const minY = Math.min(swTile[1], neTile[1]);
      const maxY = Math.max(swTile[1], neTile[1]);

      this.lastTileBounds = [];

      for (let xIndex = minX; xIndex <= maxX; xIndex++) {
        for (let yIndex = minY; yIndex <= maxY; yIndex++) {
          this.lastTileBounds.push(this.mapService.getTileId(xIndex, yIndex));
          if (0 <= xIndex && xIndex <= 255 && 0 <= yIndex && yIndex <= 127 && !this.mapService.hasTileDownloaded(xIndex, yIndex)) {
            this.mapService.getChargingStationTile(xIndex, yIndex).subscribe(() => {
              this.collectChargingStationsInsideBoundingBox();
            });
          }
        }
      }

      const minLat = Math.min(largerNE.lat, largerSW.lat);
      const maxLat = Math.max(largerNE.lat, largerSW.lat);
      const minLon = Math.min(largerNE.lng, largerSW.lng);
      const maxLon = Math.max(largerNE.lng, largerSW.lng);

      if (this.accountService.getUserSubscriptionType() != SubscriptionType.Free) {
        this.chargerStatusTimer.unsubscribe();

        this.chargerStatusTimer = timer(2000, 60000).subscribe(() => {
          if (this.chargingStationsDetailed) {
            this.mapService.getChargingStationListStatus(minLat, maxLat, minLon, maxLon, this.vehicleService.getSelectedCar().ChargerTypes).subscribe((resp) => {
              this.chargingStationBoundStatus = resp;
              this.updateSources();
            });
          } else {
            const favoriteChargerIds = this.favoriteChargingStationsInsideBoundingBox.map(charger => charger.id);
            if (favoriteChargerIds.length > 0) {
              this.mapService.getFavoriteChargingStationListStatus(favoriteChargerIds).subscribe((resp) => {
                this.chargingStationBoundStatus = resp;
                this.updateSources();
              });
            }
          }
        });
      }
    }

    if (this.chargingStationsDetailed || this.favoriteChargingStationsDetailed) {
      this.collectChargingStationsInsideBoundingBox();
    }
  }

  // ─── Collect Charging Stations & Update Sources ──────────────────────

  public setChargingStations(): void {
    if (this.chargingStationsDetailed) {
      this.collectChargingStationsInsideBoundingBox();
    }
  }

  private collectChargingStationsInsideBoundingBox(): void {
    if (!this.mapInstance || this.mapInstance.getZoom() <= 7) {
      return;
    }

    this.chargingStationsInsideBoundingBox = [];
    this.favoriteChargingStationsInsideBoundingBox = [];
    this.chargingGroupsInsideBoundingBox = [];

    const selectedCS = this.vehicleService.getChargingStations();
    const zoom = this.mapInstance.getZoom();

    for (let t = 0; t < this.lastTileBounds.length; t++) {
      const chargingStationsArray = this.mapService.getTileChargingStations()[this.lastTileBounds[t]];
      if (chargingStationsArray) {
        for (let i = 0; i < chargingStationsArray.length; i++) {
          const cs = chargingStationsArray[i];
          const isPopupStation = this.popupStationId != null && cs.id === this.popupStationId;
          if (this.lastBoundingBox &&
            this.pointInBounds(cs.lat, cs.lon, this.lastBoundingBox) &&
            cs.types && this.isArrayContainsCS(selectedCS, cs.types) &&
            (this.settingsService.getContainsFavoriteChargingStationId(cs.id) || isPopupStation || cs.minZoomLevel <= zoom) &&
            !this.isElementBigIcon(cs.lat, cs.lon) &&
            !this.routeChargingStationIds.has(cs.id)) {

            const chargingStationEl = { ...cs };

            const selectedPlugs: Plug[] = [];
            for (let j = 0; j < chargingStationEl.plugs.length; j++) {
              if (selectedCS[chargingStationEl.plugs[j].type - 1]) {
                selectedPlugs.push(chargingStationEl.plugs[j]);
              }
            }
            chargingStationEl.plugs = selectedPlugs;

            let maxpower = 0;
            for (let j = 0; j < chargingStationEl.plugs.length; j++) {
              if (chargingStationEl.plugs[j].power > maxpower) {
                maxpower = chargingStationEl.plugs[j].power;
              }
            }
            chargingStationEl.maxpower = maxpower;

            const powerSuffix = chargingStationEl.maxpower > 35 ? '3' :
              chargingStationEl.maxpower > 10 ? '2' : '1';

            // Determine station & operator states
            chargingStationEl.isFavStation = this.settingsService.getContainsFavoriteChargingStationId(chargingStationEl.id);
            chargingStationEl.isDisabledStation = this.settingsService.getContainsDisabledChargingStationId(chargingStationEl.id);
            chargingStationEl.isDisabledOperator = this.settingsService.getContainsDisabledChargingOperatorId(chargingStationEl.opid);
            chargingStationEl.isFavOperator = this.settingsService.getContainsFavoriteChargingOperatorId(chargingStationEl.opid);

            if (chargingStationEl.isFavStation) {
              chargingStationEl.img = `cs_big${powerSuffix}_favs`;
            } else if (chargingStationEl.isDisabledStation) {
              chargingStationEl.img = `cs_big${powerSuffix}_diss`;
            } else if (chargingStationEl.isDisabledOperator) {
              chargingStationEl.img = `cs_big${powerSuffix}_diso`;
            } else if (chargingStationEl.isFavOperator) {
              chargingStationEl.img = `cs_big${powerSuffix}_favo`;
            } else {
              chargingStationEl.img = `cs_big${powerSuffix}`;
            }

            if (isPopupStation) {
              this.popupStationCache = chargingStationEl;
            }

            if (chargingStationEl.isFavStation || isPopupStation) {
              this.favoriteChargingStationsInsideBoundingBox.push(chargingStationEl);
            } else {
              this.chargingStationsInsideBoundingBox.push(chargingStationEl);
            }
          }
        }
      }

      const tileGroups = this.mapService.getTileChargingGroups()[this.lastTileBounds[t]];
      if (tileGroups?.hasOwnProperty(Math.floor(zoom))) {
        const chargingGroupsArray = tileGroups[Math.floor(zoom)];
        if (chargingGroupsArray) {
          for (let i = 0; i < chargingGroupsArray.length; i++) {
            if (this.lastBoundingBox &&
              this.pointInBounds(chargingGroupsArray[i].lat, chargingGroupsArray[i].lon, this.lastBoundingBox)) {
              this.chargingGroupsInsideBoundingBox.push({ ...chargingGroupsArray[i] });
            }
          }
        }
      }
    }

    // If the popup station wasn't found in the current tiles, use the cached copy
    if (this.popupStationId && this.popupStationCache) {
      const found = this.favoriteChargingStationsInsideBoundingBox.some(s => s.id === this.popupStationId);
      if (!found) {
        this.favoriteChargingStationsInsideBoundingBox.push(this.popupStationCache);
      }
    }

    this.updateSources();
  }

  private updateSources(): void {
    const map = this.mapInstance;
    if (!map) return;

    const isLight = this.settingsService.getMap().Skin === MapSkin.Light;
    const skinSuffix = isLight ? '_light' : '';
    const skinStr = isLight ? 'light' : 'dark';

    // The _avail/_unavail pins are rasterised on demand rather than at start-up,
    // so every icon a feature references has to be materialised here first.
    const toFeature = (cs: ChargingStation): GeoJSON.Feature => {
      let iconName = cs.img + '_200' + skinSuffix;
      const status = this.chargingStationBoundStatus[cs.id];
      if (status != null) {
        iconName += status === 1 ? '_avail' : '_unavail';
      }
      this.ensureStationImage(iconName);
      const properties: any = {
        id: cs.id,
        icon: iconName
      };
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [cs.lon, cs.lat] },
        properties
      };
    };

    // Regular charging station features
    const csFeatures: GeoJSON.Feature[] = this.chargingStationsInsideBoundingBox.map(toFeature);

    // Favorite charging station features
    const favFeatures: GeoJSON.Feature[] = this.favoriteChargingStationsInsideBoundingBox.map(toFeature);

    // Group features — the cluster count is baked into the image, so each
    // distinct count needs its own. Track which ones this view actually uses so
    // the ones left behind by panning can be evicted below.
    const liveGroupImages = new Set<string>();

    const groupFeatures: GeoJSON.Feature[] = this.chargingGroupsInsideBoundingBox.map(group => {
      const imgId = `cg_${group.powerid}_${skinStr}_${group.count}`;
      liveGroupImages.add(imgId);
      this.touchGroupImage(imgId, group, skinStr, isLight);

      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [group.lon, group.lat] },
        properties: { icon: imgId }
      };
    });

    const csSource = map.getSource(CS_SOURCE) as GeoJSONSource;
    if (csSource) csSource.setData({ type: 'FeatureCollection', features: csFeatures });

    const favSource = map.getSource(CS_FAV_SOURCE) as GeoJSONSource;
    if (favSource) favSource.setData({ type: 'FeatureCollection', features: favFeatures });

    const groupSource = map.getSource(CS_GROUPS_SOURCE) as GeoJSONSource;
    if (groupSource) groupSource.setData({ type: 'FeatureCollection', features: groupFeatures });

    // After setData, so nothing being removed can still be referenced.
    this.evictGroupImages(liveGroupImages);
  }

  // ─── Charging Station Popup (delegated to MapChargingPopupService) ───

  public redrawChargingStationPopup(chargingStationPopup: ChargingStationDetails): void {
    this.mapChargingPopupService.redrawChargingStationPopup(chargingStationPopup);
  }

  public redrawOpenedChargingStationPopup(): void {
    this.mapChargingPopupService.redrawOpenedChargingStationPopup();
  }

  public setLastRouteData(routeData: Route | null): void {
    this.mapChargingPopupService.setLastRouteData(routeData);
  }

  // ─── Utility Methods ─────────────────────────────────────────────────

  private isElementBigIcon(lat: number, lon: number): boolean {
    for (let i = 0; i < this.bigChargingStations.length; i++) {
      if (this.bigChargingStations[i][0] == lat && this.bigChargingStations[i][1] == lon) {
        return true;
      }
    }
    return false;
  }

  private isArrayContainsCS(selectedCS: boolean[], stationTypes: string): boolean {
    let contains = false;
    let i = 0;
    while (!contains && i < selectedCS.length) {
      if (selectedCS[i] && stationTypes.includes((i + 1).toString())) {
        contains = true;
      }
      i++;
    }
    return contains;
  }

  private pointInBounds(lat: number, lon: number, bounds: { south: number, west: number, north: number, east: number }): boolean {
    return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
  }

  private boundsContains(outer: { south: number, west: number, north: number, east: number }, inner: LngLatBounds): boolean {
    const sw = inner.getSouthWest();
    const ne = inner.getNorthEast();
    return sw.lat >= outer.south && sw.lng >= outer.west && ne.lat <= outer.north && ne.lng <= outer.east;
  }

  private isCSTooltipClick(e: MouseEvent): boolean {
    const path = e.composedPath();
    if (path) {
      for (let i = 0; i < path.length; i++) {
        const el = path[i] as HTMLElement;
        if (el.className && typeof el.className === "string" && el.className.includes("cs-tooltip")) {
          return true;
        }
      }
    }
    return false;
  }

  public setRouteChargingStationIds(ids: string[]): void {
    this.routeChargingStationIds = new Set(ids);
    this.collectChargingStationsInsideBoundingBox();
    this.updateSources();
  }

  public redrawCanvas(): void {
    this.getExtendedBoundingBox();
  }

  /** Hide non-route charging stations during navigation */
  public hideNonRouteStations(): void {
    if (!this.mapInstance) return;
    for (const layerId of [CS_LAYER, CS_FAV_LAYER, CS_GROUPS_LAYER]) {
      if (this.mapInstance.getLayer(layerId)) {
        this.mapInstance.setLayoutProperty(layerId, 'visibility', 'none');
      }
    }
  }

  /** Show all charging station layers (when exiting navigation) */
  public showAllStations(): void {
    if (!this.mapInstance) return;
    for (const layerId of [CS_LAYER, CS_FAV_LAYER, CS_GROUPS_LAYER]) {
      if (this.mapInstance.getLayer(layerId)) {
        this.mapInstance.setLayoutProperty(layerId, 'visibility', 'visible');
      }
    }
  }
}
